import classNames from 'classnames';
import React, { forwardRef, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import Typography from '@/components/atoms/typography/typography';
import AltapayPaymentMethod from '../altapay-payment-method/altapay-payment-method';
import styles from './altapay-payment-method-group.module.scss';

export interface AltapayPaymentMethodGroupProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'children' | 'onChange' | 'required' | 'value'
  > {
  amount?: string;
  /** The control from the useForm hook */
  control?: Control;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** Indicates whether the field has an error */
  error?: string | boolean;
  /** The text to show as a help */
  help?: string;
  /** Identification for the radio button */
  id?: string;
  /** The text to show as a label */
  label?: string;
  /** The name of the field */
  name: string;
  /** A list of options */
  options?: { label: string; value: 'Dankort' | 'Mastercard' | 'Mobilepay' }[];
  /** Indicates whether the field is required */
  required?: string | boolean;
}

const AltapayPaymentMethodGroup = forwardRef<
  HTMLInputElement,
  AltapayPaymentMethodGroupProps
>(
  (
    {
      amount,
      className,
      control,
      disabled,
      error,
      help,
      label,
      name,
      options = [],
      required,
      ...rest
    },
    ref,
  ) => {
    const { field } = useController({ control, name });
    const [value, setValue] = useState(field.value || []);

    const handleChange = (parameter: string) => {
      field.onChange?.(parameter);

      setValue(parameter);
    };

    return (
      <div
        ref={ref}
        className={classNames(styles.component, className, {
          [styles.hasError]: error,
          [styles.isDisabled]: disabled,
        })}
      >
        {(label || typeof required === 'string') && (
          <div className={styles.header}>
            <Typography className={styles.label} tagStyle="labelSmall">
              {label}
            </Typography>

            {typeof required === 'string' && (
              <Typography className={styles.required} tagStyle="bodySmall">
                {required}
              </Typography>
            )}
          </div>
        )}

        <div className={styles.body}>
          {options.map((option, optionIndex) => (
            <AltapayPaymentMethod
              key={optionIndex}
              {...rest}
              checked={option.value === value}
              disabled={disabled}
              label={option.label}
              onChange={() => handleChange(option.value)}
              value={option.value}
            >
              {amount}
            </AltapayPaymentMethod>
          ))}
        </div>

        {(typeof error === 'string' || help) && (
          <Typography className={styles.help} tagStyle="bodySmall">
            {typeof error === 'string' ? error : help}
          </Typography>
        )}
      </div>
    );
  },
);

AltapayPaymentMethodGroup.displayName = 'AltapayPaymentMethodGroup';

export default AltapayPaymentMethodGroup;
